<template>
  <div class="tw-relative tw-w-full">
    <!-- Developer tooling — never inside a host's page. -->
    <t-flag-debug-banner v-if="isFlagDebugBannerAvailable && !embedded" />
    <!-- Embedded, keyed by the org in the URL: the layouts scope themselves in their mount hooks,
         so a HOST-driven move between two orgs under one layout must remount it. Standalone the key
         is constant — titan's own switcher re-scopes the mounted layout, and a remount would only
         throw its state away. -->
    <router-view :key="orgViewKey" />
    <!-- position:fixed at z-index 999999 across the full viewport — embedded it
         would paint over the HOST's own header. -->
    <vue3-progress-bar v-if="!embedded" />
    <t-crumbs-publisher v-if="embedded && routerReady" :host="host" />
    <t-company-scope-sync v-if="embedded && routerReady" />
    <t-toasts />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useHost } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import TToasts from "@/components/TToasts";
import TFlagDebugBanner from "@/components/TFlagDebugBanner";
import { useTheme } from "@/composables";
import { isEmbedded } from "@/remote/contract";
import TCrumbsPublisher from "@/remote/TCrumbsPublisher.vue";
import TCompanyScopeSync from "@/remote/TCompanyScopeSync.vue";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useHarnessFlagsBootstrap } from "@/composables/useFlags/useHarnessFlagsBootstrap";
import { useAppcuesBlockListFlagSync } from "@/composables/useAppcuesBlockListFlagSync";
import { setupDayjsLocale } from "@/plugins/i18n-dayjs";
import { useAuth0SessionStore } from "@/store/auth0Session.store";

type AuthUser = {
  accountname: string;
  email: string;
  id: string;
  twoFactorAuthEnabled?: boolean;
};

const authStore = useAuthenticationStore();
const router = useRouter();
const route = useRoute();
const i18n = useI18n();

// Snapshotted once by the contract module.
const embedded = isEmbedded();

const orgViewKey = computed(() => {
  if (!embedded) return "standalone";
  const [firstSegment = ""] = (route.path ?? "").split("/").filter(Boolean);
  return `${firstSegment}/${route.params?.orgUuid ?? ""}`;
});

// useTheme() (vueuse useDark) WRITES data-theme onto <html> — the HOST's root
// when embedded — and persists into the shared 'vueuse-color-scheme'
// localStorage key. It force-flipped a dark shell to light on every element
// mount. The host owns its theme; ensureRuntimeEnvironment only fills in a
// default when the host set none.
if (!embedded) {
  useTheme();
}
// useHost() only works on the element ROOT (it reads instance.ce), so the host is resolved here
// and handed down. Mounted only once the router has resolved — see TCrumbsPublisher for why.
const host = embedded ? useHost() : null;
const routerReady = ref(false);
if (embedded)
  void router.isReady().then(() => {
    routerReady.value = true;
  });

// Initialise Harness feature flags (no-op unless the harness source is active).
useHarnessFlagsBootstrap();
useAppcuesBlockListFlagSync();

const { isFeatureAvailable: isFlagDebugBannerAvailable } =
  useFlag("HARNESS_DEMO_FLAG");
// eslint-disable-next-line @typescript-eslint/no-explicit-any
setupDayjsLocale(i18n as any);

const authUser = computed<AuthUser | null>(
  () => authStore.authUser as AuthUser | null,
);

const { isFeatureAvailable: isAuth0LoginEnabled } = useFlag("AUTH0_LOGIN");

// Auth0 cookie session is optional; resolve defensively so a missing Pinia
// context (e.g. isolated tests) simply means "no Auth0 session". Returns null
// when the feature flag is off so the Auth0 path stays fully inert.
const getAuth0Session = () => {
  if (!isAuth0LoginEnabled.value) return null;
  try {
    return useAuth0SessionStore();
  } catch {
    return null;
  }
};

onMounted(async () => {
  await router.isReady();

  // Embedded, the HOST owns authentication. Bouncing to our own sign-in would
  // navigate the host's page away from wherever the user actually was — the
  // remote is a component on someone else's screen, not the screen.
  if (isEmbedded()) return;

  const route = router.currentRoute.value;
  const path = route?.path || "/";

  if (
    !authUser.value &&
    !path.includes("/sso/callback") &&
    !path.includes("/azure/callback") &&
    !path.includes("auth/password/set") &&
    !path.includes("/auth/sign-in") &&
    // The Logout view owns this route, including the pass Auth0 returns the
    // user to. Redirecting here would push sign-in with redirect=/logout,
    // signing the user straight back out after they log in.
    !path.includes("/logout")
  ) {
    // Hydrate the Auth0 cookie session on app load (per the RWA contract). If an
    // Auth0 session is active, the user is authenticated without a JWT — leave
    // them in place instead of bouncing to sign-in.
    const session = getAuth0Session();
    if (session) {
      await session.ensureSession();
      if (session.authenticated) {
        return;
      }
    }

    localStorage.clear();

    const queryParams: Record<string, string> = {};
    const currentFullPathForRedirect = route?.fullPath || path;
    if (
      currentFullPathForRedirect &&
      currentFullPathForRedirect !== "/" &&
      currentFullPathForRedirect.trim() !== ""
    ) {
      queryParams.redirect = currentFullPathForRedirect;
    }

    router.push({
      name: "auth-sign-in",
      query: queryParams,
    });
  }
});
</script>
